import { chargeDataFragment } from 'apollo/queries/autogenerate/operations';
import {
  InstallmentPlan,
  InstallmentPlanKind,
} from 'apollo/queries/autogenerate/schemas';
import { formatYen } from 'screens/StatementScreen/utils';

export function getCurrentInstallmentIndex(charge: chargeDataFragment) {
  let foundIndex = -1;

  charge.installmentPlanOptions?.unavailable.forEach((unavailable) => {
    foundIndex =
      unavailable.reason.details.installments?.findIndex(
        (installment) =>
          installment.id === convertChargeIdToInstallmentId(charge)
      ) ?? -1;
  });

  return foundIndex !== -1 ? foundIndex + 1 : -1;
}

export function getTotalInstallment(charge: chargeDataFragment) {
  const kind =
    charge.installmentPlanOptions?.unavailable[0]?.reason.details.kind;

  return convertInstallmentKindToNumber(kind);
}

export function isApplePayment(charge: chargeDataFragment) {
  if (charge.metadata.transactionId && charge.metadata.transactionItemId) {
    return true;
  }

  return false;
}

export function getApplePaymentChargeTitle(charge: chargeDataFragment) {
  // Apple clawbacks can be either trade-ins or a carrier discounts
  // To distinguish a clawback, the charge transactionId is different from the payment id
  const isAppleClawback = charge.payment?.id !== charge.metadata.transactionId;

  if (isAppleClawback) {
    if (charge.metadata.appleClawbackType === 'carrierDiscount') {
      return '通信キャリア割引調整額';
    }

    return '下取り調整差額';
  }

  // Charge will use charge.description as the title
  return '';
}

export const getCurrentInstallment = (charge: chargeDataFragment) => {
  if (
    !charge.installmentPlanOptions ||
    !charge.installmentPlanOptions.unavailable.length
  ) {
    return null;
  }

  const kind = charge.installmentPlanOptions.unavailable[0].reason.details.kind;
  const installments = charge.installmentPlanOptions.unavailable.find(
    (item) => item.kind === kind
  )?.reason.details.installments;

  if (!installments) {
    return null;
  }

  return installments.find(
    (item) => item.id === convertChargeIdToInstallmentId(charge)
  );
};

export const convertChargeIdToInstallmentId = (charge: chargeDataFragment) => {
  return charge.id.replace('chr_', 'ins_');
};

export function toFullWidth(number: number) {
  const value = `${number}`;

  return value.replace(/[A-Za-z0-9]/g, function (s) {
    return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
  });
}

export function getInstallmentLabel(charge: chargeDataFragment) {
  if (
    !charge.installmentPlanOptions ||
    !charge.installmentPlanOptions.unavailable.length
  ) {
    return null;
  }

  const currentIndex = getCurrentInstallmentIndex(charge);
  const totalInstallments = getTotalInstallment(charge);

  if (currentIndex < 0 || totalInstallments === 1) {
    return null;
  }

  return `${toFullWidth(currentIndex)}回目/${toFullWidth(
    totalInstallments
  )}回あと払い`;
}

// This funtion returns a label with the up-coming installment (the n-th installment)
export function getUpComingInstallmentLabel(installment: InstallmentPlan) {
  const currentChargeIndex =
    installment.installments.findIndex((charge) => charge.dueNext) + 1;

  const totalInstallments = installment.installments.length;

  // When there's no current charge (all installments dueNext are false), it means the last installment was created but its due date is still in the future, so the up-coming installment is the last installment
  return `${toFullWidth(
    currentChargeIndex || totalInstallments
  )}回目/${toFullWidth(totalInstallments)}回あと払い`;
}

// This function returns a label with installment amount and total number of installment
export function getInstallmentAmountandInstallmentLengthLabel(
  installment: InstallmentPlan
) {
  const installmentAmount = installment.installments[0].amount;
  const totalInstallments = installment.installments.length;

  return `${formatYen(installmentAmount, 'suf')}x${totalInstallments}回`;
}

export function convertInstallmentKindToNumber(
  kind: InstallmentPlanKind | undefined | null
) {
  if (!kind) {
    return 1;
  }

  switch (kind) {
    case InstallmentPlanKind.THREE_PAY:
    case InstallmentPlanKind.APPLE3_MONTHS:
      return 3;
    case InstallmentPlanKind.SIX_PAY:
    case InstallmentPlanKind.APPLE6_MONTHS:
      return 6;
    case InstallmentPlanKind.TWELVE_PAY:
    case InstallmentPlanKind.APPLE12_MONTHS:
      return 12;
    case InstallmentPlanKind.APPLE24_MONTHS:
      return 24;
    case InstallmentPlanKind.APPLE36_MONTHS:
      return 36;
    case InstallmentPlanKind.APPLE48_MONTHS:
      return 48;
    default:
      return 1;
  }
}

export function getNPayThresholdByInstallmentKind(
  kind: InstallmentPlanKind | undefined | null
) {
  if (!kind) {
    return 1;
  }

  switch (kind) {
    case InstallmentPlanKind.THREE_PAY:
      return 3000;
    case InstallmentPlanKind.SIX_PAY:
      return 6000;
    default:
      1;
  }
}
